import React, { useState } from 'react';
import { PlanBoardItem, PlanRegion } from 'types';
import { COLORS, MOSCOW_DOT, getTreeColor } from 'utils/canvasUtils';
import { DragHandle } from 'components/ui';
import PlanBoardCardPills from './PlanBoardCardPills';

export type PlanBoardCardVariant = 'list' | 'canvas';
export type ResizeEdge = 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type PlanBoardCardProps = {
    item: PlanBoardItem;
    variant: PlanBoardCardVariant;
    /** Canvas variant only: the Timeline region (see PlanRegion, types.ts) this card's current
     * placement falls inside, or null when the grid has no lines drawn yet (a single region
     * spanning the whole grid still resolves here — null only means the placement/regions lookup
     * itself came back empty, e.g. mid-drag). Shown as a small release/priority badge. */
    region?: PlanRegion | null;
    /** Starts the whole-card drag — attached to the card root so any part of it can be grabbed. */
    onCardMouseDown?: (e: React.MouseEvent) => void;
    /** Canvas variant only: starts a resize drag, from anywhere along one of the card's four
     * edges (not just a fixed handle point), or from one of its four corners for a simultaneous
     * width+height resize — see the resize-edge strips and resize-corner squares below. */
    onResizeHandleMouseDown?: (e: React.MouseEvent, edge: ResizeEdge) => void;
    /** Pressed but not yet dragged past the movement threshold: shows a "picked up" shadow in place, no layout change. */
    elevated?: boolean;
    /** Whether this card is the current selection (toggled by clicking it — see PlanPage). */
    selected?: boolean;
    /** Canvas variant only: true while this card is the source of an in-progress Ctrl+drag
     * connection (see PlanPage's useConnectDrag instance) — shown with a highlight outline,
     * mirroring CanvasNode's isConnectionSource on the OST canvas. */
    isConnectionSource?: boolean;
    /** Canvas variant only: true while this card is the currently-hovered candidate target of an
     * in-progress Ctrl+drag connection — mirrors CanvasNode's isConnectionTarget. */
    isConnectionTarget?: boolean;
    /** Canvas variant only: fired on pointer-enter while a Ctrl+drag connection may be in
     * progress — the caller (TimelineCanvas/PlanPage) decides whether one actually is. */
    onConnectHoverStart?: () => void;
    /** Canvas variant only: fired on pointer-leave, pairing with onConnectHoverStart. */
    onConnectHoverEnd?: () => void;
    /** Canvas variant only: true while a Ctrl+drag connection is in progress ANYWHERE on the
     * canvas (see PlanPage's connectDrag.active) — mirrors CanvasNode's connectMode prop. Swaps
     * this card to the pinch cursor unconditionally, same as CanvasNode does for every node while
     * connectMode is true (not just the one under the pointer). */
    connectMode?: boolean;
    /** Canvas variant only: true while Ctrl/Cmd is held (see hooks/useModifierKeyActive) —
     * combined with this card's own hover state to swap to the pinch cursor BEFORE a drag has
     * started, signaling "Ctrl+drag from here to connect" — mirrors CanvasNode's modifierKeyActive
     * prop exactly (`isPinchCursor = connectMode || (isHovered && modifierKeyActive)`). */
    modifierKeyActive?: boolean;
    /** Canvas variant only, shown while selected on an Enabler card: fired by the small X-in-a-
     * circle button in the card's top-right corner. A story/experiment card has no equivalent,
     * since removing one of those means editing the OST tree it came from, not the Plan board.
     * What this actually does depends on the Enabler card's origin (decided by the caller, see
     * PlanPage's handleRemoveEnablerCard): a standalone card (made via "Add enabler card") is
     * deleted outright, while a tree-sourced one (an OST enablerNode surfaced here, see
     * usePlanBoardItems.ts) is sent back to the Plan column instead, since it has a source OST
     * tree to return to rather than being deleted. */
    onDelete?: () => void;
    style?: React.CSSProperties;
    className?: string;
};

const RESIZE_EDGES: ResizeEdge[] = ['left', 'right', 'top', 'bottom'];
const RESIZE_CORNERS: ResizeEdge[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

/**
 * Draggable card representing a story/experiment node, shown either in the
 * Plan page's Plan column (variant='list') or placed on the Timeline grid
 * (variant='canvas'). While dragging, the card itself is
 * hidden and a plain placeholder box represents it instead — see PlanPage.tsx.
 */
export default function PlanBoardCard({
    item, variant, region, onCardMouseDown, onResizeHandleMouseDown, elevated, selected,
    isConnectionSource, isConnectionTarget, onConnectHoverStart, onConnectHoverEnd,
    connectMode, modifierKeyActive, onDelete, style, className = '',
}: PlanBoardCardProps) {
    const isDense = variant === 'canvas';
    const isEnabler = item.type === 'enabler';
    // A standalone Enabler card (made via "Add enabler card") has no tree at all (treeId/treeName
    // both null); a tree-sourced one (an OST enablerNode, see usePlanBoardItems.ts) carries a real
    // treeName just like a story/experiment card does — see EnablerCardItem's doc comment, types.ts.
    const hasTree = item.treeName !== null;
    const priorityColor = item.priority ? MOSCOW_DOT[item.priority as keyof typeof MOSCOW_DOT] : undefined;
    // Same color drives both the list variant's tree-pill fill and the canvas variant's tree
    // dot, so a given tree reads as the same color regardless of where the card is shown. A card
    // with no tree identity skips this entirely rather than computing it from a null key.
    const treeColor = item.treeName ? getTreeColor(item.treeId ?? item.treeName) : undefined;
    // Same per-type border color the OST canvas draws its nodes with (CanvasNode.tsx's
    // `c.border`) — item.type ('story' | 'experiment' | 'enabler') keys directly into the same
    // COLORS map, so a card reads with the same subtle, type-tinted outline as its OST node.
    const borderColor = COLORS[item.type]?.border;
    // Own hover state, same as CanvasNode's isHovered — tracked locally (via onMouseEnter/
    // onMouseLeave below) rather than passed in, since it's purely a per-card cursor concern the
    // caller has no other use for.
    const [isHovered, setIsHovered] = useState(false);

    // Same custom open-hand cursor as the OST canvas's nodes at rest (see CanvasNode.tsx),
    // swapping to the pinch cursor exactly when CanvasNode does: unconditionally while a
    // connect-drag is active anywhere (connectMode), or while this card is hovered with the
    // modifier held (signaling a Ctrl+drag can be started right here). Resolved via PUBLIC_URL
    // rather than a CSS url() so webpack doesn't try to bundle the .cur file. Canvas variant
    // only — the list variant keeps the browser's plain grab cursor (see .plan-board-card's own
    // `cursor: grab`) and never shows the pinch icon.
    const isPinchCursor = isDense && (!!connectMode || (isHovered && !!modifierKeyActive));
    const grabCursorStyle = isDense
        ? { cursor: `url(${process.env.PUBLIC_URL || ''}/${isPinchCursor ? 'hand-pinch-cursor.cur' : 'hand-cursor.cur'}) 8 8, grab` }
        : undefined;

    return (
        <div
            className={`plan-board-card plan-board-card--${variant} plan-board-card--content-${variant} ${isEnabler ? 'plan-board-card--enabler' : ''} ${elevated ? 'plan-board-card--elevated' : ''} ${selected ? 'plan-board-card--selected' : ''} ${isConnectionSource ? 'plan-board-card--connect-source' : ''} ${isConnectionTarget ? 'plan-board-card--connect-target' : ''} ${isPinchCursor ? 'plan-board-card--pinch' : ''} ${className}`.trim()}
            style={{ ...style, ...grabCursorStyle, borderColor }}
            onMouseDown={onCardMouseDown}
            onMouseEnter={() => {
                setIsHovered(true);
                onConnectHoverStart?.();
            }}
            onMouseLeave={() => {
                setIsHovered(false);
                onConnectHoverEnd?.();
            }}
        >
            {!isDense && <DragHandle className='plan-board-card__drag-handle' />}
            <div className='plan-board-card__body'>
                <span className='plan-board-card__title'>{item.title}</span>
                {item.description && (
                    <p className='plan-board-card__description'>{item.description}</p>
                )}
                {!isDense && (
                    <div className='plan-board-card__meta'>
                        <PlanBoardCardPills item={item} layout='inline' />
                    </div>
                )}
            </div>


            {/* Canvas variant only: priority + tree pills shrink to a pair of small dots
                straddling the card's bottom-right — see .plan-board-card__canvas-dots. A card
                with no tree identity has no dot to show for it (see hasTree above). */}
            {isDense && (
                <div className='plan-board-card__canvas-dots'>
                    {item.priority && (
                        <span
                            className='plan-board-card__canvas-dot'
                            style={{ background: priorityColor ?? 'var(--muted)' }}
                            title={item.priority}
                        />
                    )}
                    {hasTree && (
                        <span
                            className='plan-board-card__canvas-dot'
                            style={{ background: treeColor }}
                            title={item.treeName ?? undefined}
                        />
                    )}
                </div>
            )}

            {/* Canvas variant only, shown while selected, Enabler cards only: a small X-in-a-
                circle in the top-right corner. No equivalent for a story/experiment card. What it
                does depends on this card's origin (see onDelete's doc comment above and PlanPage's
                handleRemoveEnablerCard): a standalone card is deleted outright, a tree-sourced one
                is sent back to the Plan column instead — same button, different label either way.
                An inline SVG rather than a "✕" text glyph: a font glyph's visual center depends on
                font metrics/subpixel rounding, which wobbles under the Timeline canvas's
                `transform: scale(--zoom)` — an SVG drawn symmetric in its own viewBox stays
                centered at any zoom. */}
            {isDense && selected && isEnabler && onDelete && (
                <button
                    type='button'
                    className='plan-board-card__delete-btn'
                    aria-label={hasTree ? 'Send back to Plan column' : 'Delete enabler card'}
                    title={hasTree ? 'Send back to Plan column' : 'Delete enabler card'}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                >
                    <svg viewBox='0 0 10 10' width='5' height='5' aria-hidden='true'>
                        <path d='M2.3 2.3L7.7 7.7M7.7 2.3L2.3 7.7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                    </svg>
                </button>
            )}

            {/* Canvas variant only, shown while selected: the same priority + tree pills as the
                list variant's meta row (full text, not the small dots above), stacked above the
                card's top-left corner — see .plan-board-card__selected-pills. */}
            {variant === 'canvas' && selected && (
                <PlanBoardCardPills item={item} layout='stacked' />
            )}

            {/* Canvas variant only, shown while selected: four invisible strips, each running the
                FULL length of one edge (Miro/Figma-style) rather than a small fixed handle at
                the edge's midpoint — grabbing anywhere along an edge starts that edge's resize
                drag. See PlanPage.tsx's handleResizeHandleMouseDown for the drag math and
                GridCanvas's snapSize for the snapping/minimum-size clamp. Four small squares sit
                on top at the corners (higher z-index, see .plan-board-card__resize-corner) for a
                simultaneous width+height resize — without them, the edge strips alone only ever
                changed one dimension per drag, since whichever strip is under the cursor wins. */}
            {variant === 'canvas' && selected && onResizeHandleMouseDown && (
                <>
                    {RESIZE_EDGES.map((edge) => (
                        <div
                            key={edge}
                            className={`plan-board-card__resize-edge plan-board-card__resize-edge--${edge}`}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                onResizeHandleMouseDown(e, edge);
                            }}
                        />
                    ))}
                    {RESIZE_CORNERS.map((corner) => (
                        <div
                            key={corner}
                            className={`plan-board-card__resize-corner plan-board-card__resize-corner--${corner}`}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                onResizeHandleMouseDown(e, corner);
                            }}
                        />
                    ))}
                </>
            )}
        </div>
    );
}
